import React, { useState, useEffect } from "react";
import { Megaphone, Cloud, Shield, Tag, Info, Clock, Check, ChevronRight } from "lucide-react";
import { useAuth } from "../../../../context/AuthContext";
import { useCreateEventContext } from "../../../../context/CreateEventContext";
import {
  getAnnouncementsForEvent,
  markAnnouncementAsRead,
} from "../../../../firebaseFunctions/announcements/announcementService";
import { isDemoMode } from "../../../../demo/isDemoMode";
import demoStore from "../../../../demo/demoStore";

const categoryConfig = {
  general:   { icon: Info,   color: "bg-blue-100 text-blue-700 border-blue-200",    label: "General" },
  weather:   { icon: Cloud,  color: "bg-yellow-100 text-yellow-700 border-yellow-200", label: "Weather" },
  security:  { icon: Shield, color: "bg-red-100 text-red-700 border-red-200",        label: "Security" },
  promotion: { icon: Tag,    color: "bg-green-100 text-green-700 border-green-200",  label: "Promotion" },
};

const formatDate = (date) => {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60)    return "Just now";
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const AnnouncementCard = ({ announcement, userId, onMarkAsRead }) => {
  const [expanded, setExpanded] = useState(false);
  const config = categoryConfig[announcement.category] || categoryConfig.general;
  const Icon = config.icon;
  const isRead = announcement.readBy?.includes(userId) || announcement.isRead;

  const handleClick = async () => {
    setExpanded(e => !e);
    if (!isRead) onMarkAsRead(announcement.id);
  };

  return (
    <div
      onClick={handleClick}
      className={`border rounded-xl overflow-hidden cursor-pointer transition-all ${
        isRead ? "bg-white border-grey-200 hover:border-grey-300" : "bg-blue-50 border-blue-200 hover:border-blue-300"
      }`}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${config.color.split(' ').slice(0,2).join(' ')}`}>
            <Icon className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${config.color}`}>
                {config.label}
              </span>
              {!isRead && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500 text-white">
                  New
                </span>
              )}
            </div>
            <p className="font-semibold text-grey-900 text-sm mb-1">{announcement.title}</p>
            <span className="flex items-center gap-1 text-xs text-grey-500">
              <Clock className="h-3 w-3" />
              {formatDate(announcement.createdAt)}
            </span>
          </div>
          <ChevronRight className={`h-4 w-4 text-grey-400 flex-shrink-0 mt-1 transition-transform ${expanded ? "rotate-90" : ""}`} />
        </div>

        {expanded && (
          <div className="mt-4 pt-4 border-t border-grey-200">
            <p className="text-sm text-grey-700 whitespace-pre-wrap">{announcement.message}</p>
            {isRead && (
              <div className="mt-3 flex items-center gap-1 text-xs text-green-600">
                <Check className="h-3 w-3" />
                Read
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Announcements = () => {
  const { user } = useAuth();
  const { eventData } = useCreateEventContext();
  const eventId = eventData?.eventId;

  const [announcements, setAnnouncements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!eventId) return;
    const load = async () => {
      setIsLoading(true);
      try {
        let result;
        if (isDemoMode) {
          // Filter directly by eventId in demo mode
          const all = demoStore.getDocs('announcements', [
            { type: 'where', field: 'eventId', op: '==', value: eventId },
          ]);
          const items = all
            .map(a => ({
              id: a.id,
              ...a.data,
              isRead: a.data.readBy?.includes(user?.uid) ?? false,
              createdAt: a.data.createdAt instanceof Date ? a.data.createdAt : new Date(a.data.createdAt),
            }))
            .sort((a, b) => b.createdAt - a.createdAt);
          result = { success: true, announcements: items };
        } else {
          result = await getAnnouncementsForEvent(eventId);
          if (result.success) {
            result.announcements = result.announcements.map(a => ({
              ...a,
              isRead: a.readBy?.includes(user?.uid) ?? false,
            }));
          }
        }
        if (result.success) setAnnouncements(result.announcements);
      } catch (err) {
        console.error("Announcements load error:", err);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [eventId, user?.uid]);

  const handleMarkAsRead = async (announcementId) => {
    if (!user?.uid) return;
    await markAnnouncementAsRead(announcementId, user.uid);
    setAnnouncements(prev =>
      prev.map(a => a.id === announcementId ? { ...a, isRead: true } : a)
    );
  };

  const unreadCount = announcements.filter(a => !a.isRead).length;

  if (isLoading) {
    return (
      <div className="w-full p-6 flex items-center justify-center min-h-[200px]">
        <div className="w-6 h-6 border-2 border-grey-300 border-t-grey-700 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full p-4 sm:p-6">
      <div className="flex items-center gap-3 mb-6">
        <Megaphone className="h-5 w-5 text-grey-700" />
        <h2 className="text-lg font-semibold text-grey-900">Announcements</h2>
        {unreadCount > 0 && (
          <span className="px-2 py-0.5 bg-blue-500 text-white text-xs font-semibold rounded-full">
            {unreadCount} new
          </span>
        )}
      </div>

      {announcements.length === 0 ? (
        <div className="text-center py-14 bg-grey-50 rounded-xl border border-grey-200">
          <Megaphone className="h-10 w-10 text-grey-300 mx-auto mb-3" />
          <p className="text-grey-500 text-sm">No announcements yet from the organizer.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {announcements.map(a => (
            <AnnouncementCard
              key={a.id}
              announcement={a}
              userId={user?.uid}
              onMarkAsRead={handleMarkAsRead}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Announcements;
